import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiXCircle, FiClock, FiMapPin, FiPhone } from 'react-icons/fi';
import Swal from 'sweetalert2';
import MiniCalendar from '../../components/MiniCalendar';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function ApproveJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [techList, setTechList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // States สำหรับ React Approval Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedTechIds, setSelectedTechIds] = useState([]);
  const [price, setPrice] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // ดึงงานทั้งหมด
      const resJobs = await fetch(`${API_URL}?action=get_all_jobs`);
      const dataJobs = await resJobs.json();
      if (dataJobs.success) {
        // กรองเอาเฉพาะงานที่รออนุมัติ
        setJobs(dataJobs.data.filter(j => j.status === 'pending_approval'));
      }

      // ดึงรายชื่อช่าง
      const resTech = await fetch(`${API_URL}?action=get_users`);
      const dataTech = await resTech.json();
      setTechList(dataTech);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const openApproveModal = (job) => {
    setSelectedJob(job);
    setSelectedTechIds(job.technicianId ? job.technicianId.split(',').filter(Boolean) : []);
    setPrice(job.price || '');
    setDate(job.appointmentDate || '');
    setTime(job.appointmentTime || '09:00');
    setIsModalOpen(true);
  };

  const submitApproval = async () => {
    if (selectedTechIds.length === 0 || !date || !time) {
      Swal.fire('แจ้งเตือน', 'กรุณาเลือกช่างอย่างน้อย 1 คน, วันที่ และเวลาให้ครบถ้วน!', 'warning');
      return;
    }

    Swal.fire({ title: 'กำลังบันทึก...', didOpen: () => Swal.showLoading() });
    
    // ค้นหาข้อมูลช่างทุกคน
    const selectedTechs = techList.filter(t => selectedTechIds.includes(t.uid));
    const techNames = selectedTechs.map(t => t.name).join(', ');
    const techPhones = selectedTechs.map(t => t.phone || '-').join(', ');

    try {
      const res = await fetch(`${API_URL}?action=update_job`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ 
          id: selectedJob.id, 
          technicianId: selectedTechIds.join(','), 
          technicianName: techNames,
          technicianPhone: techPhones,
          price: price, 
          appointmentDate: date, 
          appointmentTime: time,
          status: 'pending' // เปลี่ยนสถานะเป็นรอเดินทาง
        })
      });
      const result = await res.json();
      if (result.success) {
        Swal.fire('สำเร็จ!', 'จ่ายงานให้ช่างเรียบร้อยแล้ว', 'success');
        setIsModalOpen(false);
        fetchData(); // โหลดข้อมูลใหม่
      } else {
        Swal.fire('ผิดพลาด', result.message || 'ไม่สามารถบันทึกข้อมูลได้', 'error');
      }
    } catch (error) {
      Swal.fire('ผิดพลาด', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้', 'error');
    }
  };

  const handleReject = (jobId) => {
    Swal.fire({
      title: 'ยืนยันการปฏิเสธ?',
      text: 'คุณต้องการยกเลิกคำขอจองคิวนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'ใช่, ปฏิเสธงาน',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: 'กำลังยกเลิก...', didOpen: () => Swal.showLoading() });
        await fetch(`${API_URL}?action=update_job`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ id: jobId, status: 'cancelled' })
        });
        Swal.fire('สำเร็จ', 'ยกเลิกงานเรียบร้อยแล้ว', 'success');
        fetchData();
      }
    });
  };

  const getJobTypeBadge = (type) => {
    const types = {
      'aircon': { label: '❄️ แอร์บ้าน', style: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50' },
      'electric': { label: '⚡ ไฟฟ้าระบบ', style: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50' },
      'contractor': { label: '🏗️ งานรับเหมา', style: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800/50' },
      'extension': { label: '🔨 งานต่อเติม', style: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-400 dark:border-fuchsia-800/50' },
      'solar': { label: '☀️ โซล่าเซลล์', style: 'bg-yellow-50 text-yellow-750 border-yellow-250 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800/50' },
      'door': { label: '🚪 ประตูออโต้', style: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800/50' },
      'evertor': { label: '🛗 ระบบลิฟท์', style: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800/50' },
      'camera': { label: '📹 กล้องวงจร', style: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800/50' },
      'rework': { label: '🔧 งานแก้', style: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50' }
    };
    const t = types[type] || { label: '💼 ทั่วไป', style: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
    return (
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border inline-flex items-center gap-1 ${t.style}`}>
        {t.label}
      </span>
    );
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-left">
      <div>
        <h1 className="text-2xl font-black text-gray-800 dark:text-white">✅ อนุมัติการจองจากเว็บ</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">ตรวจสอบคำขอจากลูกค้า ประเมินราคา และจ่ายงานให้ช่าง</p>
      </div>

      {isLoading ? (
        <div className="text-center py-20 font-bold text-blue-500">กำลังโหลดข้อมูล...</div>
      ) : jobs.length === 0 ? (
        <div className="bg-white dark:bg-[#1e293b] p-10 rounded-2xl text-center border border-gray-200 dark:border-[#334155] shadow-sm text-gray-400">
          <p className="text-4xl mb-4">📭</p>
          <p className="font-bold">ไม่มีรายการรออนุมัติในขณะนี้</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {jobs.map(job => (
            <div key={job.id} className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-sm border border-orange-200 dark:border-orange-900/30 overflow-hidden relative flex flex-col">
              <div className="bg-orange-50 dark:bg-orange-900/20 px-4 py-2 border-b border-orange-100 dark:border-orange-900/30 flex justify-between items-center">
                <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 flex items-center gap-1"><FiClock /> รอการอนุมัติ</span>
                <span className="text-[10px] text-gray-400">{job.createdAt}</span>
              </div>
              
              <div className="p-5 flex-1">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-black text-lg text-gray-900 dark:text-white">{job.customerName}</h3>
                    <a href={`tel:${job.phone}`} className="text-xs font-bold text-blue-500 flex items-center gap-1 mt-0.5"><FiPhone /> {job.phone || '-'}</a>
                  </div>
                  {getJobTypeBadge(job.job_type)}
                </div>

                <div className="bg-gray-50 dark:bg-[#0f172a] p-3 rounded-xl mb-4 border border-gray-100 dark:border-[#334155]">
                  <p className="text-[10px] font-bold text-gray-400 mb-1">อาการเสีย / รายละเอียด</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{job.problem || 'ไม่ได้ระบุ'}</p>
                </div>

                <div className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                  <FiMapPin className="shrink-0 mt-0.5 text-red-400" />
                  <p className="line-clamp-2">{job.address || 'ไม่ได้ระบุที่อยู่'}</p>
                </div>

                {job.appointmentDate && (
                  <div className="mt-3 bg-blue-50 dark:bg-blue-950/20 text-blue-650 dark:text-blue-400 font-bold border border-blue-100 dark:border-blue-900/30 p-2.5 rounded-xl text-[10px] flex items-center justify-between">
                    <span className="flex items-center gap-1">📅 วันนัดที่ลูกค้าเลือก:</span>
                    <span>
                      {new Date(job.appointmentDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {job.appointmentTime ? ` (${job.appointmentTime === '09:00' ? 'ช่วงเช้า' : job.appointmentTime === '13:00' ? 'ช่วงบ่าย' : job.appointmentTime === '16:30' ? 'ช่วงเย็น' : job.appointmentTime})` : ' (รอยืนยันเวลา)'}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-100 dark:border-[#334155] flex gap-2">
                <button onClick={() => handleReject(job.id)} className="w-1/3 py-2.5 rounded-xl text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 transition">
                  ปฏิเสธ
                </button>
                <button onClick={() => openApproveModal(job)} className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition flex justify-center items-center gap-1 shadow-md">
                  <FiCheckCircle /> จ่ายงานให้ช่าง
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 📅 React Modal สำหรับอนุมัติและจ่ายงานช่าง */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in text-gray-800 text-left">
          <div className="bg-white dark:bg-[#131825] dark:text-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden">
            
            <div className="bg-blue-600 text-white p-5 flex justify-between items-center border-b border-blue-700">
              <div>
                <h3 className="font-black text-sm sm:text-base flex items-center gap-2">
                  ✅ อนุมัติและจ่ายงานให้ทีมช่าง
                </h3>
                <p className="text-[10px] text-blue-200 mt-0.5 font-bold">ลูกค้า: {selectedJob.customerName} | เบอร์: {selectedJob.phone}</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="bg-blue-700/50 hover:bg-blue-800 text-white transition p-2 rounded-full"
              >
                <FiXCircle size={16} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
              
              {/* 📋 รายละเอียดข้อมูลลูกค้าที่จองเข้ามา */}
              <div className="bg-blue-50/50 dark:bg-blue-950/20 p-4 rounded-xl border border-blue-200 dark:border-blue-900/30 space-y-2.5 text-xs text-left">
                <h4 className="font-black text-blue-700 dark:text-blue-400 border-b border-blue-200/50 dark:border-blue-900/30 pb-2 flex items-center gap-1.5">
                  📋 รายละเอียดข้อมูลลูกค้าที่จองบริการ
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700 dark:text-gray-300">
                  <p><span className="font-bold text-gray-400">👤 ชื่อลูกค้า:</span> <span className="font-black text-gray-900 dark:text-white">{selectedJob.customerName}</span></p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-400">📞 เบอร์โทร:</span> 
                    <span className="font-black text-indigo-600 dark:text-indigo-400 text-sm select-all bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded border border-indigo-200/40 dark:border-indigo-800/30">{selectedJob.phone || '-'}</span>
                    <a 
                      href={`tel:${selectedJob.phone}`} 
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-2 py-1 rounded-md text-[10px] inline-flex items-center gap-1 transition shadow-sm"
                    >
                      <FiPhone size={10} /> โทรออก
                    </a>
                  </div>
                  <p className="flex items-center gap-1.5"><span className="font-bold text-gray-400">🛠️ ประเภทงาน:</span> {getJobTypeBadge(selectedJob.job_type)}</p>
                  <p><span className="font-bold text-gray-400">📅 นัดหมายที่ลูกค้าเลือก:</span> <span className="font-bold text-gray-800 dark:text-gray-200">{selectedJob.appointmentDate ? `${new Date(selectedJob.appointmentDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })} (${selectedJob.appointmentTime === '09:00' ? 'ช่วงเช้า' : selectedJob.appointmentTime === '13:00' ? 'ช่วงบ่าย' : selectedJob.appointmentTime === '16:30' ? 'ช่วงเย็น' : selectedJob.appointmentTime || 'รอยืนยันเวลา'})` : '-'}</span></p>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed"><span className="font-bold text-gray-400">📍 ที่อยู่หน้างาน:</span> <span className="font-medium text-gray-800 dark:text-gray-200">{selectedJob.address || 'ไม่ได้ระบุที่อยู่'}</span></p>
                <div className="bg-white dark:bg-[#0f172a] p-3 rounded-lg border border-gray-200 dark:border-[#334155] text-gray-800 dark:text-gray-200 font-medium">
                  <span className="block text-[9px] font-bold text-gray-400 uppercase mb-1">🛠️ รายละเอียดปัญหา / อาการเสีย:</span>
                  <p className="text-xs">{selectedJob.problem || 'ไม่ได้ระบุอาการเสีย'}</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold mb-1.5 text-gray-500 dark:text-gray-400">เลือกทีมช่างผู้รับผิดชอบ (เลือกได้หลายคน) <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-gray-50 dark:bg-[#0f172a] p-3 rounded-lg border border-gray-300 dark:border-[#334155] max-h-32 overflow-y-auto custom-scrollbar">
                  {techList.map(t => {
                    const isChecked = selectedTechIds.includes(t.uid);
                    return (
                      <label key={t.uid} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-200 dark:hover:bg-[#1e293b] rounded transition text-xs">
                        <input 
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              setSelectedTechIds(selectedTechIds.filter(id => id !== t.uid));
                            } else {
                              setSelectedTechIds([...selectedTechIds, t.uid]);
                            }
                          }}
                          className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                        />
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{t.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-500 dark:text-gray-400">ราคาประเมินเบื้องต้น (บาท)</label>
                  <input 
                    type="number" 
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-xs rounded-lg outline-none" 
                    placeholder="เช่น 1500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-500 dark:text-gray-400">เวลาที่นัดหมาย <span className="text-red-500">*</span></label>
                  <select 
                    value={time}
                    onChange={e => setTime(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-xs rounded-lg outline-none font-bold"
                  >
                    <option value="09:00">ช่วงเช้า (09:00 - 12:00 น.)</option>
                    <option value="13:00">ช่วงบ่าย (13:00 - 16:00 น.)</option>
                    <option value="16:30">ช่วงเย็น (16:30 - 18:30 น.)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold mb-2 text-gray-500 dark:text-gray-400">เลือกวันที่นัดหมาย (สีเขียว = ว่าง, สีแดง = เต็ม) <span className="text-red-500">*</span></label>
                <MiniCalendar 
                  selectedDate={date} 
                  onChange={dateStr => setDate(dateStr)} 
                  allowPastDates={true} 
                />
                {date && (
                  <div className="mt-3 bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold border border-blue-200 dark:border-blue-900/30 p-2.5 rounded-xl text-xs text-center">
                    📅 วันที่เลือก: {new Date(date).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-[#1a2130] p-4 flex justify-end gap-2 border-t border-gray-200 dark:border-[#334155]">
              <button 
                type="button" 
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-[#2d3748] dark:hover:bg-[#3d485e] text-gray-700 dark:text-gray-300 font-bold rounded-lg text-xs transition"
              >
                ยกเลิก
              </button>
              <button 
                type="button" 
                onClick={submitApproval}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition shadow-md"
              >
                บันทึก & จ่ายงาน
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}